import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { LOGO, PHONE, PHONE_HREF, SERVICE_AREA, IMAGES, SLOGAN } from "@/lib/site";

export default function Contact({ onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <footer id="contact" ref={ref} className="relative overflow-hidden bg-black pt-24 md:pt-32" data-testid="contact-section">
      <div className="absolute inset-0 opacity-25">
        <motion.img style={{ y }} src={IMAGES.location} alt="Salt Lake City" className="h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/70" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-xs uppercase tracking-mega text-gold"
        >
          Serving on the water
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 font-serif font-light leading-[0.82] tracking-tighter text-white text-6xl sm:text-8xl md:text-[9rem] lg:text-[12rem]"
        >
          Salt Lake<br />
          <span className="text-gold-gradient italic">City</span>
        </motion.h2>

        <p className="mt-8 max-w-md text-base md:text-lg font-light text-white/60">
          {SERVICE_AREA}. Booking beyond the lake? Reach out — we travel for the right vessel.
        </p>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-white/10 pt-12">
          <a href={PHONE_HREF} className="group flex flex-col gap-2" data-testid="contact-phone">
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40">
              <Phone size={14} /> Call
            </span>
            <span className="font-serif text-2xl md:text-3xl text-white group-hover:text-gold transition-colors">{PHONE}</span>
          </a>
          <a href="mailto:parkernicolas2004@gmail.com" className="group flex flex-col gap-2" data-testid="contact-email">
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40">
              <Mail size={14} /> Email
            </span>
            <span className="font-serif text-2xl md:text-3xl text-white group-hover:text-gold transition-colors break-all">
              parkernicolas2004@gmail.com
            </span>
          </a>
          <div className="flex flex-col gap-2">
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40">
              <MapPin size={14} /> Area
            </span>
            <span className="font-serif text-2xl md:text-3xl text-white">Utah & beyond</span>
          </div>
        </div>

        <div className="mt-16">
          <button
            onClick={() => onNavigate("#booking")}
            className="group inline-flex items-center gap-3 border border-gold/40 px-10 py-5 text-xs uppercase tracking-[0.25em] text-gold hover:bg-gold hover:text-black transition-colors duration-300"
            data-testid="contact-cta"
          >
            Request Your Quote
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </button>
        </div>

        <div className="mt-24 flex flex-col md:flex-row items-center justify-between gap-6 border-t border-white/10 py-10">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Black Anchor Collective" className="h-11 w-11 object-contain" />
            <div className="flex flex-col">
              <span className="font-serif tracking-[0.25em] text-white/80">BLACK ANCHOR COLLECTIVE</span>
              <span className="font-serif italic text-sm text-gold" data-testid="footer-slogan">{SLOGAN}.</span>
            </div>
          </div>
          <p className="text-xs tracking-[0.2em] text-white/30 uppercase">
            © {new Date().getFullYear()} Black Anchor Collective · <Link to="/admin" className="hover:text-gold transition-colors" data-testid="footer-staff-login">Staff</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
